import { useEffect, useMemo, useState } from "react";
import { Disc, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, fmtQty } from "@/lib/api";
import { Combobox } from "@/components/common";

function SourcesDialog({ size, onPick, onClose }) {
  const [items, setItems] = useState(null);
  const [sel, setSel] = useState([]);
  const [need, setNeed] = useState("");
  const toggle = (d) => setSel((s) => (s.some((x) => x.id === d.id) ? s.filter((x) => x.id !== d.id) : [...s, d]));
  useEffect(() => { api.get("/cutting/sources", { params: { cable_size: size } }).then((r) => setItems(r.data)); }, [size]);
  const groups = useMemo(() => {
    const g = {};
    (items || []).forEach((d) => { (g[d.product_id] ||= { brand: d.brand, name: d.product_name, uom: d.uom, list: [] }).list.push(d); });
    return Object.entries(g).sort((a, b) => a[1].brand.localeCompare(b[1].brand));
  }, [items]);
  const best = useMemo(() => {
    const n = +need;
    if (!n || !items) return null;
    return items.filter((d) => d.available >= n).sort((a, b) => a.available - b.available)[0]?.id || null;
  }, [need, items]);
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="w-[calc(100vw-1.5rem)] max-w-lg max-h-[85vh] overflow-y-auto overflow-x-hidden" data-testid="size-sources-dialog">
        <DialogHeader><DialogTitle>{size} · available stock</DialogTitle></DialogHeader>
        {items === null && <div className="text-sm text-muted-foreground">Loading…</div>}
        {items && groups.length > 0 && <div className="flex items-center gap-2 text-sm">
          <span className="text-muted-foreground shrink-0">Customer needs</span>
          <Input type="number" inputMode="decimal" min="0" value={need} onChange={(e) => setNeed(e.target.value)} placeholder="metres" className="h-9 w-28" data-testid="size-need-input" />
          <span className="text-xs text-muted-foreground">{best ? "Best-fit piece marked below" : need ? "No single piece is long enough" : "to find the smallest piece that fits"}</span>
        </div>}
        {items && !groups.length && <div className="text-sm text-muted-foreground" data-testid="size-sources-empty">No drum or offcut available for this size.</div>}
        <div className="space-y-4 min-w-0">{groups.map(([pid, g]) => (
          <div key={pid} className="min-w-0" data-testid={`size-group-${g.brand.replace(/\s+/g, "-")}`}>
            <div className="flex justify-between text-sm font-semibold"><span>{g.brand}</span><span className="font-mono">{fmtQty(g.list.reduce((s, d) => s + d.available, 0), g.uom)}</span></div>
            <div className="text-xs text-muted-foreground mb-2 truncate">{g.name}</div>
            <div className="grid grid-cols-2 gap-2">{g.list.map((d) => (
              <button type="button" key={d.id} onClick={() => toggle(d)} data-testid={`size-source-${d.drum_code}`}
                className={`relative min-w-0 text-left rounded-md border p-2.5 transition-colors ${sel.some((x) => x.id === d.id) ? "border-red-600 bg-red-600/10 ring-1 ring-red-600" : best === d.id ? "border-emerald-600 ring-1 ring-emerald-600" : "hover:border-red-600 hover:bg-red-600/5"}`}>
                {sel.some((x) => x.id === d.id) && <Check className="absolute top-1.5 right-1.5 h-4 w-4 text-red-600" />}
                {best === d.id && <span className="absolute bottom-1.5 right-1.5 rounded bg-emerald-600 px-1.5 text-[9px] font-semibold uppercase text-white" data-testid="size-best-fit">Best fit</span>}
                <div className="flex items-center gap-1 font-mono text-sm font-semibold truncate"><Disc className="h-3 w-3 shrink-0" />{d.mfr_drum_no || d.parent_mfr_drum_no || d.drum_code}{d.kind === "offcut" && <span className="ml-1 text-[9px] uppercase text-amber-600">offcut</span>}</div>
                <div className="text-[11px] text-muted-foreground truncate font-mono">{d.drum_code}</div>
                <div className="font-mono text-base font-semibold mt-1">{fmtQty(d.available, d.uom)}</div>
              </button>
            ))}</div>
          </div>
        ))}</div>
        {groups.length > 0 && <div className="sticky bottom-0 -mx-6 -mb-6 px-6 py-3 bg-background border-t flex items-center gap-2">
          <span className="text-xs text-muted-foreground mr-auto">Tap one or more drums</span>
          <Button disabled={!sel.length} onClick={() => onPick(sel.map((d) => ({ ...d, _cut: need && sel.length === 1 ? need : "" })))} className="bg-red-600 hover:bg-red-700 text-white" data-testid="size-sources-add-btn">Add {sel.length || ""} selected</Button>
        </div>}
      </DialogContent>
    </Dialog>
  );
}

export function SizeQuickPick({ onPick, refreshKey }) {
  const [opts, setOpts] = useState([]);
  const [size, setSize] = useState("");
  useEffect(() => { api.get("/cutting/options").then((r) => setOpts(r.data)); }, [refreshKey]);
  const sizes = useMemo(() => {
    const m = {};
    opts.forEach((o) => { const e = (m[o.cable_size] ||= { brands: new Set(), qty: 0, uom: o.uom }); e.brands.add(o.brand); e.qty += o.available; });
    return Object.entries(m).sort((a, b) => parseFloat(a[0]) - parseFloat(b[0]) || a[0].localeCompare(b[0]))
      .map(([s, e]) => ({ value: s, label: `${s} · ${[...e.brands].join(", ")} · ${fmtQty(Math.round(e.qty * 1000) / 1000, e.uom)}` }));
  }, [opts]);
  return (
    <>
      <Combobox options={sizes} value={size} onChange={setSize} testid="cutting-quick-size-select" placeholder={sizes.length ? "Select size to see all brands & drums" : "No stock available"} />
      {size && <SourcesDialog size={size} onClose={() => setSize("")} onPick={(ds) => { setSize(""); onPick(ds); }} />}
    </>
  );
}
